import { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Typography,
  Paper,
  Chip,
} from "@mui/material";
import { Save } from "@mui/icons-material";
import { useRecordContext, useNotify, useRefresh } from "react-admin";
import { apiUrl } from "../../../httpClient";
import { handleApiError } from "../../../utils/errorHandler";
import type {
  BusinessRulesResponseDto,
  UpdateBusinessRulesDto,
  Environment,
} from "../../../types/api";

export const ProviderBusinessRules = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const [businessRules, setBusinessRules] = useState<
    BusinessRulesResponseDto | null
  >(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedEnv, setSelectedEnv] = useState<Environment>("production");
  const [formData, setFormData] = useState<UpdateBusinessRulesDto>({
    isActive: true,
    markupPercentage: undefined,
    timeoutSearchMs: undefined,
    timeoutBookingMs: undefined,
    timeoutRoomsMs: undefined,
    timeoutCancellationMs: undefined,
    maxRetryAttempts: undefined,
    defaultCurrency: undefined,
    maxAdvanceBookingDays: undefined,
    minAdvanceBookingDays: undefined,
    priceIncreasePercent: undefined,
    customRules: {},
  });

  useEffect(() => {
    if (record?.id) {
      loadBusinessRules();
    }
  }, [record?.id, selectedEnv]);

  useEffect(() => {
    if (businessRules) {
      setFormData({
        isActive: businessRules.isActive ?? true,
        markupPercentage: businessRules.markupPercentage,
        timeoutSearchMs: businessRules.timeoutSearchMs,
        timeoutBookingMs: businessRules.timeoutBookingMs,
        timeoutRoomsMs: businessRules.timeoutRoomsMs,
        timeoutCancellationMs: businessRules.timeoutCancellationMs,
        maxRetryAttempts: businessRules.maxRetryAttempts,
        defaultCurrency: businessRules.defaultCurrency,
        maxAdvanceBookingDays: businessRules.maxAdvanceBookingDays,
        minAdvanceBookingDays: businessRules.minAdvanceBookingDays,
        priceIncreasePercent: businessRules.priceIncreasePercent,
        customRules: businessRules.customRules || {},
      });
    }
  }, [businessRules]);

  const loadBusinessRules = async () => {
    if (!record?.id) return;

    setLoading(true);
    try {
      const response = await fetch(
        apiUrl(
          `/providers/${record.id}/business-rules${
            selectedEnv ? `?environment=${selectedEnv}` : ""
          }`,
        ),
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("tn_admin_access")}`,
          },
        },
      );

      if (response.ok) {
        const data = await response.json();
        // API returns array, get first item or use object directly
        const rules = Array.isArray(data) ? data[0] || null : data;
        setBusinessRules(rules);
      } else if (response.status === 404) {
        setBusinessRules(null);
      }
    } catch (error) {
      const appError = handleApiError(error);
      notify(appError.message, { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!record?.id) return;

    setSaving(true);
    try {
      const updateData: UpdateBusinessRulesDto = {
        ...formData,
        environment: selectedEnv,
      };

      const response = await fetch(
        apiUrl(`/providers/${record.id}/business-rules`),
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("tn_admin_access")}`,
          },
          body: JSON.stringify(updateData),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to update business rules");
      }

      notify("Business rules updated successfully", { type: "success" });
      loadBusinessRules();
      refresh();
    } catch (error) {
      const appError = handleApiError(error);
      notify(appError.message, { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  if (!record) return null;

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Environment</InputLabel>
          <Select
            value={selectedEnv}
            onChange={(e) => setSelectedEnv(e.target.value as Environment)}
            label="Environment"
          >
            <MenuItem value="production">Production</MenuItem>
            <MenuItem value="development">Development</MenuItem>
          </Select>
        </FormControl>
        <Button
          startIcon={<Save />}
          onClick={handleSave}
          variant="contained"
          disabled={saving}
        >
          {saving ? "Saving..." : "Save Rules"}
        </Button>
      </Box>

      {loading ? (
        <Typography>Loading business rules...</Typography>
      ) : (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive ?? true}
                  onChange={(e) =>
                    setFormData({ ...formData, isActive: e.target.checked })
                  }
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body1">Provider Active</Typography>
                  <Typography variant="caption" color="text.secondary">
                    When disabled, this provider will not be used in searches
                  </Typography>
                </Box>
              }
            />

            <TextField
              label="Markup (%)"
              type="number"
              value={formData.markupPercentage ?? ""}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  markupPercentage: e.target.value
                    ? parseFloat(e.target.value)
                    : undefined,
                })
              }
              fullWidth
              helperText="Markup percentage as decimal (0.07 = 7%)"
            />

            <TextField
              label="Search Timeout (ms)"
              type="number"
              value={formData.timeoutSearchMs ?? ""}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  timeoutSearchMs: e.target.value
                    ? parseInt(e.target.value)
                    : undefined,
                })
              }
              fullWidth
            />

            <TextField
              label="Booking Timeout (ms)"
              type="number"
              value={formData.timeoutBookingMs ?? ""}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  timeoutBookingMs: e.target.value
                    ? parseInt(e.target.value)
                    : undefined,
                })
              }
              fullWidth
            />

            <TextField
              label="Rooms Timeout (ms)"
              type="number"
              value={formData.timeoutRoomsMs ?? ""}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  timeoutRoomsMs: e.target.value
                    ? parseInt(e.target.value)
                    : undefined,
                })
              }
              fullWidth
            />

            <TextField
              label="Cancellation Timeout (ms)"
              type="number"
              value={formData.timeoutCancellationMs ?? ""}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  timeoutCancellationMs: e.target.value
                    ? parseInt(e.target.value)
                    : undefined,
                })
              }
              fullWidth
            />

            <TextField
              label="Max Retry Attempts"
              type="number"
              value={formData.maxRetryAttempts ?? ""}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  maxRetryAttempts: e.target.value
                    ? parseInt(e.target.value)
                    : undefined,
                })
              }
              fullWidth
            />

            <TextField
              label="Default Currency"
              value={formData.defaultCurrency ?? ""}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  defaultCurrency: e.target.value || undefined,
                })
              }
              fullWidth
            />

            <TextField
              label="Max Advance Booking Days"
              type="number"
              value={formData.maxAdvanceBookingDays ?? ""}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  maxAdvanceBookingDays: e.target.value
                    ? parseInt(e.target.value)
                    : undefined,
                })
              }
              fullWidth
            />

            <TextField
              label="Min Advance Booking Days"
              type="number"
              value={formData.minAdvanceBookingDays ?? ""}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  minAdvanceBookingDays: e.target.value
                    ? parseInt(e.target.value)
                    : undefined,
                })
              }
              fullWidth
            />

            <TextField
              label="Price Increase %"
              type="number"
              value={formData.priceIncreasePercent ?? ""}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  priceIncreasePercent: e.target.value
                    ? parseFloat(e.target.value)
                    : undefined,
                })
              }
              fullWidth
            />

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Custom Rules (JSON)
              </Typography>
              <TextField
                multiline
                rows={8}
                value={JSON.stringify(formData.customRules || {}, null, 2)}
                onChange={(e) => {
                  try {
                    const parsed = JSON.parse(e.target.value);
                    setFormData({ ...formData, customRules: parsed });
                  } catch {
                    // Invalid JSON, keep as is
                  }
                }}
                fullWidth
                helperText="Additional rules as JSON object"
                error={
                  formData.customRules !== undefined &&
                  typeof formData.customRules !== "object"
                }
              />
            </Box>

            {businessRules && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Last updated: {new Date(businessRules.updatedAt).toLocaleString()}
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>
      )}
    </Box>
  );
};
